import {
  api,
  apiFetchResponse,
  clearApiCache,
} from "./client";
import type { Role, UserStatus } from "@/lib/auth/access";
import type { WorkbookOverlay } from "@/lib/mapper/types";
import type {
  AdminUserMutationResponse,
  AdminUserRow,
  CategoryAttribute,
  CategoryNode,
  CreateBlankWorkbookRequest,
  CreateBlankWorkbookResponse,
  CreateWorkbookExportRequest,
  CreateWorkbookExportResponse,
  DictionaryRecord,
  ExportSummary,
  ExportJob,
  ExportJobListItem,
  GenerateAIRequest,
  GenerateAIResponse,
  ImportWorkbookResponse,
  ModuleUsageResponse,
  PreviewResponse,
  ProfileRecord,
  ProfileWriteRequest,
  ReconfigureSheetResponse,
  ReseedSettingsResponse,
  SaveWorkbookSheetRequest,
  SettingPutResponse,
  SettingsResponse,
  SheetBinding,
  ShopifyConnectionRequest,
  ShopifySaveResponse,
  ShopifyStatus,
  ShopifyTestResponse,
  TestAIConnectionResponse,
  UploadedWorkbookResponse,
  WorkbookGetResponse,
  WorkbookPreviewRequest,
  WorkbookSheetResponse,
  WorkbooksListResponse,
  Workspace,
  WorkspaceMembersResponse,
  WorkspaceRole,
  Invitation,
} from "./types";
import type { ImportConfig } from "@/lib/mapper/types";

const SHORT_TTL_MS = 60_000;

type QueryValue = string | number | boolean | null | undefined;

function enc(value: string): string {
  return encodeURIComponent(value);
}

function withQuery(
  path: string,
  params: Record<string, QueryValue> = {},
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

function taxonomyFetchInit() {
  return { cacheTtlMs: SHORT_TTL_MS };
}

export async function listWorkbooks(): Promise<WorkbooksListResponse> {
  return api.get<WorkbooksListResponse>("/api/workbooks");
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  return api.get<AdminUserRow[]>("/api/admin/users");
}

export async function inviteAdminUser(body: {
  email: string;
  role: Role;
}): Promise<AdminUserMutationResponse> {
  const result = await api.post<AdminUserMutationResponse>(
    "/api/admin/users",
    body,
  );
  clearApiCache("/api/admin/users");
  return result;
}

export async function setAdminUserRole(body: {
  email: string;
  role: Role;
}): Promise<AdminUserMutationResponse> {
  const result = await api.patch<AdminUserMutationResponse>(
    "/api/admin/users/role",
    body,
  );
  clearApiCache("/api/admin/users");
  return result;
}

export async function setAdminUserStatus(body: {
  email: string;
  status: UserStatus;
}): Promise<AdminUserMutationResponse> {
  const result = await api.patch<AdminUserMutationResponse>(
    "/api/admin/users/status",
    body,
  );
  clearApiCache("/api/admin/users");
  return result;
}

export async function deleteAdminUser(
  email: string,
): Promise<AdminUserMutationResponse> {
  const result = await api.delete<AdminUserMutationResponse>(
    withQuery("/api/admin/users", { email }),
  );
  clearApiCache("/api/admin/users");
  return result;
}

export async function getWorkbook(id: string): Promise<WorkbookGetResponse> {
  return api.get<WorkbookGetResponse>(`/api/workbooks/${enc(id)}`);
}

/**
 * Upload a workbook. `picks` carries the user's explicit choices from the
 * import screen: which sheets to keep and the template for each (null = no
 * template). With picks, the backend binds exactly those templates and never
 * guesses one from the sheet's detected product type.
 */
export async function uploadWorkbook(
  formData: FormData,
  configs?: Record<string, ImportConfig>,
  picks?: { sheets: string[]; templates: Record<string, string | null> },
): Promise<UploadedWorkbookResponse> {
  if (configs && Object.keys(configs).length > 0) {
    formData.set("configs", JSON.stringify(configs));
  }
  if (picks) {
    formData.set("sheets", JSON.stringify(picks.sheets));
    formData.set("bindings", JSON.stringify(picks.templates));
  }
  const result = await api.post<UploadedWorkbookResponse>("/api/workbooks", formData);
  clearApiCache("/api/workbooks");
  return result;
}

export async function createBlankWorkbook(
  args: CreateBlankWorkbookRequest = {},
): Promise<CreateBlankWorkbookResponse> {
  const result = await api.post<CreateBlankWorkbookResponse>(
    "/api/workbooks/blank",
    args,
  );
  clearApiCache("/api/workbooks");
  return result;
}

export async function importWorkbook(args: {
  id: string;
  sheet: string;
  mode: "replace-source" | "new";
  file: File;
  configs?: Record<string, ImportConfig>;
}): Promise<ImportWorkbookResponse> {
  const formData = new FormData();
  formData.append("sheet", args.sheet);
  formData.append("mode", args.mode);
  formData.append("file", args.file);
  if (args.configs && Object.keys(args.configs).length > 0) {
    // Send the per-sheet map for mode:"new" (BE create() uses it).
    formData.append("configs", JSON.stringify(args.configs));
    // Send the single config for the target sheet for mode:"replace-source".
    const singleConfig =
      args.configs[args.sheet] ?? Object.values(args.configs)[0];
    if (singleConfig) {
      formData.append("importConfig", JSON.stringify(singleConfig));
    }
  }

  const result = await api.post<ImportWorkbookResponse>(
    `/api/workbooks/${enc(args.id)}/import`,
    formData,
  );

  clearApiCache("/api/workbooks");
  clearApiCache(`/api/workbooks/${enc(args.id)}`);
  clearApiCache(`/api/workbooks/${enc(args.id)}/sheets/${enc(args.sheet)}`);
  clearApiCache(
    `/api/workbooks/${enc(args.id)}/sheets/${enc(args.sheet)}/overlay`,
  );
  if (result.workbookId) {
    clearApiCache(`/api/workbooks/${enc(result.workbookId)}`);
  }

  return result;
}

export async function previewWorkbook(
  workbookId: string,
  body: WorkbookPreviewRequest,
): Promise<PreviewResponse> {
  return api.post<PreviewResponse>(
    `/api/workbooks/${enc(workbookId)}/preview`,
    body,
  );
}

export async function createWorkbookExport(
  workbookId: string,
  body: CreateWorkbookExportRequest = {},
): Promise<CreateWorkbookExportResponse> {
  return api.post<CreateWorkbookExportResponse>(
    `/api/workbooks/${enc(workbookId)}/exports`,
    body,
  );
}

export async function getWorkbookExportsSummary(
  workbookId: string,
  body: { sheet?: string } = {},
): Promise<ExportSummary> {
  return api.post<ExportSummary>(
    `/api/workbooks/${enc(workbookId)}/exports/summary`,
    body,
  );
}

export async function getWorkbookSheet(
  workbookId: string,
  sheet: string,
  params: { limit?: number; offset?: number } = {},
): Promise<WorkbookSheetResponse> {
  return api.get<WorkbookSheetResponse>(
    withQuery(`/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}`, {
      limit: params.limit,
      offset: params.offset,
    }),
  );
}

export async function getOverlay(
  workbookId: string,
  sheet: string,
): Promise<WorkbookOverlay> {
  return api.get<WorkbookOverlay>(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}/overlay`,
  );
}

export async function putOverlay(
  workbookId: string,
  sheet: string,
  overlay: WorkbookOverlay,
): Promise<WorkbookOverlay> {
  const result = await api.put<WorkbookOverlay>(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}/overlay`,
    overlay,
  );
  clearApiCache(`/api/workbooks/${enc(workbookId)}`);
  clearApiCache(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}/overlay`,
  );
  return result;
}

export async function saveWorkbookSheet(
  workbookId: string,
  sheet: string,
  body: SaveWorkbookSheetRequest,
): Promise<WorkbookSheetResponse> {
  const result = await api.put<WorkbookSheetResponse>(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}`,
    body,
  );
  clearApiCache(`/api/workbooks/${enc(workbookId)}`);
  clearApiCache(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}`,
  );
  return result;
}

export async function setWorkbookSheetBinding(
  workbookId: string,
  sheet: string,
  profileId: string | null,
) {
  return api.put<SheetBinding>(
    `/api/workbooks/${enc(workbookId)}/sheets/${enc(sheet)}/binding`,
    { profileId },
  );
}

export async function listProfiles(
  params: {
    type?: string;
    customerId?: string | null;
  } = {},
): Promise<ProfileRecord[]> {
  return api.get<ProfileRecord[]>(
    withQuery("/api/profiles", {
      type: params.type,
      customerId: params.customerId,
    }),
  );
}

export async function getProfile(id: string): Promise<ProfileRecord> {
  return api.get<ProfileRecord>(`/api/profiles/${enc(id)}`);
}

export async function createProfile(
  body: ProfileWriteRequest,
): Promise<ProfileRecord> {
  return api.post<ProfileRecord>("/api/profiles", body);
}

export async function updateProfile(
  id: string,
  body: ProfileWriteRequest,
): Promise<ProfileRecord> {
  return api.put<ProfileRecord>(`/api/profiles/${enc(id)}`, body);
}

export async function deleteProfile(id: string): Promise<void> {
  await api.delete<void>(`/api/profiles/${enc(id)}`);
}

export async function listExportJobs(
  params: {
    workbookId?: string;
    limit?: number;
  } = {},
): Promise<ExportJobListItem[]> {
  return api.get<ExportJobListItem[]>(
    withQuery("/api/exports/jobs", {
      workbookId: params.workbookId,
      limit: params.limit,
    }),
  );
}

export async function getExportJob(id: string): Promise<ExportJob> {
  return api.get<ExportJob>(`/api/exports/jobs/${enc(id)}`);
}

export async function cancelExportJob(id: string): Promise<{ ok: true }> {
  return api.patch<{ ok: true }>(`/api/exports/jobs/${enc(id)}`, {
    action: "cancel",
  });
}

/** Same-origin download link; the session + active-workspace cookies authorize it. */
export function getExportJobDownloadUrl(id: string): string {
  return `/api/exports/jobs/${enc(id)}/download`;
}

export async function downloadExportJobBlob(id: string): Promise<Blob> {
  const response = await apiFetchResponse(
    `/api/exports/jobs/${enc(id)}/download`,
  );
  return response.blob();
}

export async function generateAI(
  body: GenerateAIRequest,
): Promise<GenerateAIResponse> {
  return api.post<GenerateAIResponse>("/api/ai/generate", body);
}

export async function testAIConnection(body: {
  provider: GenerateAIRequest["provider"];
}): Promise<TestAIConnectionResponse> {
  return api.post<TestAIConnectionResponse>("/api/ai/test-connection", body);
}

export async function getSettings(): Promise<SettingsResponse> {
  return api.get<SettingsResponse>("/api/settings", {
    cacheTtlMs: SHORT_TTL_MS,
  });
}

export async function putSetting(
  key: string,
  value: unknown,
): Promise<SettingPutResponse> {
  const result = await api.put<SettingPutResponse>("/api/settings", {
    key,
    value,
  });
  clearApiCache("/api/settings");
  return result;
}

export async function reseedSettings(): Promise<ReseedSettingsResponse> {
  const result = await api.post<ReseedSettingsResponse>(
    "/api/settings/reseed",
    {},
  );
  clearApiCache("/api/settings");
  clearApiCache("/api/profiles");
  return result;
}

export async function getShopifyStatus(): Promise<ShopifyStatus> {
  return api.get<ShopifyStatus>("/api/shopify");
}

export async function saveShopifyConnection(
  body: ShopifyConnectionRequest,
): Promise<ShopifySaveResponse> {
  return api.put<ShopifySaveResponse>("/api/shopify", body);
}

export async function testShopifyConnection(
  body: ShopifyConnectionRequest,
): Promise<ShopifyTestResponse> {
  return api.post<ShopifyTestResponse>("/api/shopify", body);
}

export async function listGoogleCategories(
  params: {
    q?: string;
    parent?: string | null;
  } = {},
): Promise<CategoryNode[]> {
  return api.get<CategoryNode[]>(
    withQuery("/api/taxonomy/google/categories", params),
    taxonomyFetchInit(),
  );
}

export async function listShopifyCategories(
  params: {
    q?: string;
    parent?: string | null;
    ancestorsOf?: string;
  } = {},
): Promise<CategoryNode[]> {
  return api.get<CategoryNode[]>(
    withQuery("/api/taxonomy/shopify/categories", params),
    taxonomyFetchInit(),
  );
}

export async function getShopifyCategoryAttributes(
  category: string,
): Promise<CategoryAttribute[]> {
  return api.get<CategoryAttribute[]>(
    withQuery("/api/taxonomy/shopify/attributes", { category }),
    taxonomyFetchInit(),
  );
}

export async function listDictionaries(): Promise<DictionaryRecord[]> {
  return api.get<DictionaryRecord[]>("/api/dictionaries");
}

export async function putDictionary(
  body: DictionaryRecord,
): Promise<DictionaryRecord> {
  return api.put<DictionaryRecord>("/api/dictionaries", body);
}

export async function deleteDictionary(params: {
  id?: string;
  slug?: string;
}): Promise<{ deleted: true }> {
  return api.delete<{ deleted: true }>(
    withQuery("/api/dictionaries", params),
  );
}

export async function getModuleUsage(): Promise<ModuleUsageResponse> {
  return api.get<ModuleUsageResponse>("/api/modules/usage");
}

// --- Workspaces (multi-tenant memberships) ---
// The /api/workspaces controller is NOT workspace-guarded, so these calls
// don't require an active workspace; the header is harmless if present.

/** Workspaces the current user belongs to, with their role in each. */
export async function listWorkspaces(): Promise<Workspace[]> {
  return api.get<Workspace[]>("/api/workspaces");
}

/** Create a new workspace (the caller becomes its OWNER). */
export async function createWorkspace(name: string): Promise<Workspace> {
  const result = await api.post<Workspace>("/api/workspaces", { name });
  clearApiCache("/api/workspaces");
  return result;
}

/** Pending workspace invitations addressed to the current user's email. */
export async function listMyInvitations(): Promise<Invitation[]> {
  return api.get<Invitation[]>("/api/workspaces/invitations/mine");
}

/** Members + pending invitations for a workspace (caller must be a member). */
export async function listWorkspaceMembers(
  id: string,
): Promise<WorkspaceMembersResponse> {
  return api.get<WorkspaceMembersResponse>(
    `/api/workspaces/${enc(id)}/members`,
  );
}

/** Invite an email to a workspace (caller must be OWNER/ADMIN). */
export async function inviteToWorkspace(
  id: string,
  body: { email: string; workspaceRole: WorkspaceRole },
) {
  const result = await api.post(
    `/api/workspaces/${enc(id)}/invitations`,
    body,
  );
  clearApiCache(`/api/workspaces/${enc(id)}/members`);
  return result;
}

/** Accept a pending invitation to a workspace for the current user. */
export async function acceptInvitation(id: string): Promise<Workspace> {
  const result = await api.post<Workspace>(
    `/api/workspaces/${enc(id)}/accept`,
    {},
  );
  clearApiCache("/api/workspaces");
  clearApiCache("/api/workspaces/invitations/mine");
  return result;
}

/** Remove a member from a workspace (caller must be OWNER/ADMIN). */
export async function removeWorkspaceMember(
  id: string,
  userId: string,
): Promise<{ ok: true }> {
  const result = await api.delete<{ ok: true }>(
    `/api/workspaces/${enc(id)}/members/${enc(userId)}`,
  );
  clearApiCache(`/api/workspaces/${enc(id)}/members`);
  return result;
}

/**
 * POST /api/workbooks/:id/sheets/:sheet/reconfigure
 *
 * Re-applies a new ImportConfig to the original upload.
 * Requires originalFileKey on the workbook (returns 409 if absent).
 *
 * With dryRun=true, returns the count of overlay rows that would be dropped
 * without actually persisting the change. Use this for the pre-apply warning.
 */
export async function reconfigureSheetImport(args: {
  id: string;
  sheet: string;
  config: ImportConfig;
  dryRun?: boolean;
}): Promise<ReconfigureSheetResponse> {
  const result = await api.post<ReconfigureSheetResponse>(
    `/api/workbooks/${enc(args.id)}/sheets/${enc(args.sheet)}/reconfigure`,
    { config: args.config, dryRun: args.dryRun ?? false },
  );

  if (!args.dryRun) {
    clearApiCache("/api/workbooks");
    clearApiCache(`/api/workbooks/${enc(args.id)}`);
    clearApiCache(
      `/api/workbooks/${enc(args.id)}/sheets/${enc(args.sheet)}`,
    );
    clearApiCache(
      `/api/workbooks/${enc(args.id)}/sheets/${enc(args.sheet)}/overlay`,
    );
  }

  return result;
}

